import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ShieldAlert, RefreshCw, Filter, Users as UsersIcon, Inbox,
  Brain, HeartPulse, Layers, Beaker, ExternalLink, Clock, AlertCircle,
  PhoneCall,
} from 'lucide-react';
import EmailDeliveryStatusWidget from '@/components/admin/EmailDeliveryStatusWidget';
import BoundaryViolationsSection from '@/components/admin/BoundaryViolationsSection';
import GovernanceHotSpotsTile from '@/components/admin/GovernanceHotSpotsTile';

const CATEGORY_LABEL = {
  account_access_vs_status: 'Account access ↔ status',
  crm_vs_lifecycle: 'CRM pipeline ↔ lifecycle',
  lifecycle_vs_decode: 'Lifecycle ↔ DECODE',
  lifecycle_vs_protocol: 'Lifecycle ↔ active protocol',
};

const SEVERITY_STYLE = {
  high: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  low: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

// ── Read-only navigation map ──────────────────────────────────
// Each entry returns an array of {to, label, icon, testid} based on the
// drift item. Returning [] hides links gracefully when target ids are missing.
const buildNavLinks = (item) => {
  const links = [];
  const subId = item.subject_id;
  const inqId = item.inquiry_id;

  switch (item.category) {
    case 'account_access_vs_status': {
      if (subId) {
        links.push({
          to: '/admin/users',
          label: 'Open User',
          icon: UsersIcon,
          testid: 'nav-open-user',
        });
      }
      if (inqId) {
        links.push({
          to: '/admin/crm',
          label: 'Open CRM',
          icon: Inbox,
          testid: 'nav-open-crm',
        });
      }
      break;
    }
    case 'crm_vs_lifecycle': {
      if (inqId) {
        links.push({
          to: '/admin/crm',
          label: 'Open CRM',
          icon: Inbox,
          testid: 'nav-open-crm',
        });
      }
      if (subId) {
        links.push({
          to: '/admin/assigned',
          label: 'Open Assigned',
          icon: UsersIcon,
          testid: 'nav-open-assigned',
        });
      }
      break;
    }
    case 'conversion_mismatch': {
      if (subId) {
        links.push({
          to: '/admin/users',
          label: 'Open User',
          icon: UsersIcon,
          testid: 'nav-open-user',
        });
      }
      if (inqId) {
        links.push({
          to: '/admin/crm',
          label: 'Open CRM',
          icon: Inbox,
          testid: 'nav-open-crm',
        });
      }
      break;
    }
    case 'lifecycle_vs_decode': {
      if (subId) {
        links.push({
          to: '/admin/decode-wizard',
          label: 'Open DECODE Workspace',
          icon: Brain,
          testid: 'nav-open-decode',
        });
        links.push({
          to: '/admin/care-ops',
          label: 'Open Care Dashboard',
          icon: HeartPulse,
          testid: 'nav-open-care-dashboard',
        });
      }
      break;
    }
    case 'lifecycle_vs_protocol': {
      if (subId) {
        links.push({
          to: '/admin/executable-protocol',
          label: 'Open Executable Protocol',
          icon: Layers,
          testid: 'nav-open-protocol',
        });
        links.push({
          to: '/admin/care-ops',
          label: 'Open Care Dashboard',
          icon: HeartPulse,
          testid: 'nav-open-care-dashboard',
        });
      }
      break;
    }
    case 'intake_assignment_readiness': {
      if (subId) {
        links.push({
          to: '/admin/biocontext-memory',
          label: 'Open Biological Context Memory',
          icon: Beaker,
          testid: 'nav-open-biocontext',
        });
        links.push({
          to: '/admin/assigned',
          label: 'Open Assigned',
          icon: UsersIcon,
          testid: 'nav-open-assigned',
        });
      }
      break;
    }
    default:
      break;
  }
  return links;
};

// ── F16 Conversion Backfill (admin-only, dry-run first) ────────
function ConversionBackfillSection({ outstanding, onApplied }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [notes, setNotes] = useState('');

  const runDryRun = async () => {
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/governance/conversion-mismatch/backfill`,
        { dry_run: true, apply: false, notes: notes || null },
        { withCredentials: true },
      );
      setResult({ ...r.data, mode: 'dry_run' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    } finally {
      setRunning(false);
    }
  };

  const applyBackfill = async () => {
    if (!result || result.candidates_count === 0) return;
    if (!confirm(`Apply backfill to ${result.candidates_count} subscribers? This writes a canonical conversion_event for each. Cannot be auto-undone.`)) return;
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/governance/conversion-mismatch/backfill`,
        { dry_run: false, apply: true, notes: notes || null },
        { withCredentials: true },
      );
      setResult({ ...r.data, mode: 'applied' });
      toast.success(`Backfill applied to ${r.data.applied} subscribers`);
      onApplied && onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-lg border border-violet-500/30 bg-violet-500/5 p-4"
      data-testid="conversion-backfill-section"
    >
      <div className="flex items-start gap-3">
        <Brain className="w-4 h-4 text-violet-300 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm uppercase tracking-wider text-violet-200">
              Conversion Event Backfill
            </h3>
            <Badge className="bg-violet-500/15 text-violet-200 border border-violet-500/30 text-[10px] uppercase tracking-wider">
              admin-only
            </Badge>
            <span className="text-[10px] text-slate-400">
              {outstanding} conversion_mismatch row{outstanding === 1 ? '' : 's'} outstanding
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Synthesise the canonical <code className="text-slate-300">conversion_event</code> for every CRM
            <code className="text-slate-300"> SUBSCRIBER_CONVERTED</code> inquiry whose user is missing one.
            Existing events are never overwritten. Lifecycle, access, DECODE, protocol, perception, and support
            are <strong>not</strong> touched.
          </p>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes (e.g. 'Pre-Q2 demo cleanup')"
              data-testid="backfill-notes-input"
              className="flex-1 min-w-[220px] bg-slate-900/60 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600"
            />
            <Button
              size="sm"
              onClick={runDryRun}
              disabled={running}
              data-testid="backfill-dry-run-btn"
              className="h-7 px-3 text-[11px] bg-slate-800 text-slate-200 border border-slate-600 hover:bg-slate-700"
            >
              {running ? 'Running…' : 'Run Dry-Run'}
            </Button>
            {result && result.mode === 'dry_run' && result.candidates_count > 0 && (
              <Button
                size="sm"
                onClick={applyBackfill}
                disabled={running}
                data-testid="backfill-apply-btn"
                className="h-7 px-3 text-[11px] bg-emerald-700/40 text-emerald-100 border border-emerald-500/40 hover:bg-emerald-700/60"
              >
                {running ? 'Applying…' : `Apply Backfill (${result.candidates_count})`}
              </Button>
            )}
          </div>

          {result && (
            <div
              className="mt-3 text-xs text-slate-300 bg-slate-900/40 rounded-md border border-slate-800 p-3"
              data-testid={`backfill-result-${result.mode}`}
            >
              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
                <span>mode: <code className="text-slate-200">{result.mode}</code></span>
                <span>inquiries: <code className="text-slate-200">{result.inquiries_total}</code></span>
                <span>candidates: <code className="text-slate-200">{result.candidates_count}</code></span>
                <span>already converted: <code className="text-slate-200">{result.already_converted_count}</code></span>
                <span>missing users: <code className="text-slate-200">{result.missing_user_count}</code></span>
                {result.mode === 'applied' && (
                  <span>applied: <code className="text-emerald-300">{result.applied}</code></span>
                )}
              </div>
              {result.candidates && result.candidates.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[11px] text-slate-400 hover:text-slate-200">
                    Show first {Math.min(result.candidates.length, 8)} candidates
                  </summary>
                  <ul className="mt-1.5 space-y-0.5 ml-2">
                    {result.candidates.slice(0, 8).map(c => (
                      <li key={c.subscriber_id} className="text-[11px] text-slate-400">
                        · {c.name || c.subscriber_id}
                        {c.email && <span className="text-slate-600"> · {c.email}</span>}
                        <span className="text-slate-600"> · {c.inquiry_id}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Postponed Onboardings tile + list (read-only) ──────────────
function PostponedOnboardingsSection({ loading, atLimit, nearLimit, summary, canCreateFollowup, onFollowupCreated }) {
  const total = (atLimit?.length || 0) + (nearLimit?.length || 0);
  if (!loading && total === 0) {
    // Hide entirely when nothing to show — keeps page calm
    return null;
  }
  return (
    <div className="mb-8" data-testid="postponed-onboardings-section">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-amber-400" />
        <h2 className="text-sm uppercase tracking-wider text-slate-300">Postponed Onboardings</h2>
        <span className="text-[10px] text-slate-500">read-only · Perception Shift cap = 3</span>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div
          data-testid="postponed-at-limit-tile"
          className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-300 mb-1">
            <AlertCircle className="w-3.5 h-3.5" /> At limit (3+)
          </div>
          <div className="text-2xl text-white" data-testid="postponed-at-limit-count">
            {summary?.at_limit_count ?? atLimit?.length ?? 0}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Cap reached — banner is now persistent for these subscribers.
          </div>
        </div>
        <div
          data-testid="postponed-near-limit-tile"
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-300 mb-1">
            <Clock className="w-3.5 h-3.5" /> Near limit (2)
          </div>
          <div className="text-2xl text-white" data-testid="postponed-near-limit-count">
            {summary?.near_limit_count ?? nearLimit?.length ?? 0}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            One more postponement away from the cap — early outreach window.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 px-1">Loading postponed onboardings…</div>
      ) : (
        <div className="space-y-2">
          {[...(atLimit || []), ...(nearLimit || [])].map((row) => (
            <PostponedRow
              key={row.subscriber_id}
              row={row}
              canCreateFollowup={canCreateFollowup}
              onFollowupCreated={onFollowupCreated}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PostponedRow({ row, canCreateFollowup, onFollowupCreated }) {
  const atLimit = !!row.remind_limit_reached;
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  // Local follow-up state mirrors the row but lets us update without a refetch
  const [followup, setFollowup] = useState({
    id: row.followup_task_id || null,
    created_at: row.followup_created_at || null,
    priority: row.followup_priority || null,
  });

  // Keep local in sync if parent refetches (server is the source of truth)
  useEffect(() => {
    setFollowup({
      id: row.followup_task_id || null,
      created_at: row.followup_created_at || null,
      priority: row.followup_priority || null,
    });
  }, [row.followup_task_id, row.followup_created_at, row.followup_priority]);

  const handleCreateFollowup = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await axios.post(
        `${API}/governance/postponed-onboardings/${row.subscriber_id}/create-followup`,
        {},
        { withCredentials: true },
      );
      const taskId = r.data?.task_id;
      const already = !!r.data?.already_exists;
      // Update local state immediately so the UI swaps without waiting for refetch
      setFollowup({
        id: taskId,
        created_at: r.data?.created_at || r.data?.task?.created_at || new Date().toISOString(),
        priority: r.data?.priority || r.data?.task?.priority || null,
      });
      toast.success(
        already ? 'Existing follow-up task is already open.' : 'Follow-up task created',
        taskId ? {
          action: {
            label: 'Open task',
            onClick: () => navigate(`/admin/care-flow?task_id=${encodeURIComponent(taskId)}`),
          },
        } : undefined,
      );
      onFollowupCreated && onFollowupCreated(row.subscriber_id, taskId);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message)
        || (typeof detail === 'string' && detail)
        || 'Could not create follow-up task.';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid={`postponed-row-${row.subscriber_id}`}
      className={`rounded-lg border p-4 flex items-start gap-3 ${
        atLimit
          ? 'border-rose-500/25 bg-rose-500/[0.03]'
          : 'border-amber-500/25 bg-amber-500/[0.03]'
      }`}
    >
      <Badge
        className={`${
          atLimit ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                  : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        } border text-[10px] uppercase tracking-wider`}
      >
        {row.remind_later_count}/{row.remind_later_max} postponed
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-white font-medium truncate">{row.name || row.subscriber_id}</span>
          {row.email && <span className="text-xs text-slate-500 truncate">· {row.email}</span>}
        </div>
        <div className="text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1 mb-2">
          <span>status: <code className="text-slate-300">{row.status || '—'}</code></span>
          {row.lifecycle_stage && (
            <span>lifecycle: <code className="text-slate-300">{row.lifecycle_stage}</code></span>
          )}
          {row.last_remind_later_at && (
            <span>last postponed: <code className="text-slate-300">
              {new Date(row.last_remind_later_at).toLocaleString()}
            </code></span>
          )}
          {row.caregivers && row.caregivers.length > 0 && (
            <span>
              caregivers: <code className="text-slate-300">
                {row.caregivers.map(c => c.name || c.email).filter(Boolean).join(', ')}
              </code>
            </span>
          )}
        </div>
        <div
          className="flex flex-wrap gap-2 pt-1 items-center"
          data-testid={`postponed-actions-${row.subscriber_id}`}
        >
          {followup.id ? (
            <div
              className="inline-flex items-center gap-2"
              data-testid={`postponed-followup-existing-${row.subscriber_id}`}
            >
              <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] uppercase tracking-wider">
                Follow-up created{followup.created_at ? ` · ${new Date(followup.created_at).toLocaleDateString()}` : ''}
              </Badge>
              <Link
                to={`/admin/care-flow?task_id=${encodeURIComponent(followup.id)}`}
                data-testid={`postponed-open-followup-${row.subscriber_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                           border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-200
                           hover:bg-emerald-500/15 hover:border-emerald-500/40 transition"
              >
                <PhoneCall className="w-3.5 h-3.5" />
                <span>Open Follow-up</span>
                <ExternalLink className="w-3 h-3 opacity-60" />
              </Link>
            </div>
          ) : (
            canCreateFollowup && (
              <Button
                size="sm"
                onClick={handleCreateFollowup}
                disabled={creating}
                data-testid={`postponed-create-followup-${row.subscriber_id}`}
                className="h-7 px-3 text-[11px] bg-emerald-700/30 text-emerald-200 hover:bg-emerald-700/40 border border-emerald-500/30"
              >
                <PhoneCall className="w-3.5 h-3.5 mr-1.5" />
                {creating ? 'Creating…' : 'Create Follow-up Task'}
              </Button>
            )
          )}
          <Link
            to="/admin/assigned"
            data-testid={`postponed-open-assigned-${row.subscriber_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                       border border-slate-700 bg-slate-900/60 text-xs text-slate-200
                       hover:bg-slate-800 hover:border-slate-600 transition"
          >
            <UsersIcon className="w-3.5 h-3.5" />
            <span>Open Assigned</span>
            <ExternalLink className="w-3 h-3 opacity-60" />
          </Link>
          <Link
            to={`/admin/care-dashboard/${row.subscriber_id}`}
            data-testid={`postponed-open-care-dashboard-${row.subscriber_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                       border border-slate-700 bg-slate-900/60 text-xs text-slate-200
                       hover:bg-slate-800 hover:border-slate-600 transition"
          >
            <HeartPulse className="w-3.5 h-3.5" />
            <span>Open Care Dashboard</span>
            <ExternalLink className="w-3 h-3 opacity-60" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function GovernanceDrift() {
  const { user } = useAuth();
  const canCreateFollowup = ['admin', 'counselor', 'health_coach'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const boundaryRef = useRef(null);
  const [items, setItems] = useState([]);
  const [byCat, setByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [postponed, setPostponed] = useState({ at_limit: [], near_limit: [], summary: {} });
  const [postponedLoading, setPostponedLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/governance/drift`, { withCredentials: true });
      setItems(r.data.items || []);
      setByCat(r.data.by_category || {});
    } catch (e) {
      toast.error(`Failed to load governance drift: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPostponed = async () => {
    setPostponedLoading(true);
    try {
      const r = await axios.get(`${API}/governance/postponed-onboardings`, { withCredentials: true });
      setPostponed({
        at_limit: r.data.at_limit || [],
        near_limit: r.data.near_limit || [],
        summary: r.data.summary || {},
      });
    } catch (e) {
      // Soft-fail: not all roles can read this — keep page usable
      setPostponed({ at_limit: [], near_limit: [], summary: {} });
    } finally {
      setPostponedLoading(false);
    }
  };

  useEffect(() => { load(); loadPostponed(); }, []);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(i => i.category === filter);
  }, [items, filter]);

  return (
    <AdminLayout>
      <div className="px-8 py-8 max-w-[1200px] mx-auto" data-testid="governance-drift-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ShieldAlert className="w-6 h-6 text-amber-400" />
              <h1 className="text-2xl font-medium text-white">Governance Drift</h1>
              <Badge className="bg-slate-700 text-slate-300 text-xs">Read-only</Badge>
            </div>
            <p className="text-sm text-slate-400 max-w-2xl">
              Subscribers and inquiries whose parallel state machines are out of sync.
              This page is purely visibility — no writes happen here. Use the per-item
              navigation links to open the relevant module and review the record manually.
            </p>
          </div>
          <Button
            data-testid="refresh-drift-btn"
            onClick={() => { load(); loadPostponed(); }}
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Email Delivery Diagnostics (admin-only, full variant) */}
        {isAdmin && (
          <div className="mb-8">
            <EmailDeliveryStatusWidget variant="full" />
          </div>
        )}

        {/* Governance Hot Spots V1 (admin-only) — placed directly above Boundary Violations */}
        {isAdmin && <GovernanceHotSpotsTile boundaryRef={boundaryRef} />}

        {/* Boundary Violations (Part 4 P1 sweep, admin-only) */}
        {isAdmin && <BoundaryViolationsSection ref={boundaryRef} />}

        {/* Summary chips */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <button
            data-testid="filter-all"
            onClick={() => setFilter('all')}
            className={`text-left rounded-lg border p-4 transition ${
              filter === 'all'
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'
            }`}
          >
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Total drift</div>
            <div className="text-2xl text-white">{items.length}</div>
          </button>
          {Object.entries(CATEGORY_LABEL).map(([key, label]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setFilter(key)}
              className={`text-left rounded-lg border p-4 transition ${
                filter === key
                  ? 'border-amber-500/40 bg-amber-500/5'
                  : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'
              }`}
            >
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
              <div className="text-2xl text-white">{byCat[key] || 0}</div>
            </button>
          ))}
        </div>

        {/* Postponed Onboardings (F15+) — read-only */}
        <PostponedOnboardingsSection
          loading={postponedLoading}
          atLimit={postponed.at_limit}
          nearLimit={postponed.near_limit}
          summary={postponed.summary}
          canCreateFollowup={canCreateFollowup}
          onFollowupCreated={loadPostponed}
        />

        {/* F16 Backfill — admin-only one-shot cleanup of historical CRM SUBSCRIBER_CONVERTED inquiries */}
        {isAdmin && (byCat?.conversion_mismatch || 0) > 0 && (
          <ConversionBackfillSection
            outstanding={byCat.conversion_mismatch}
            onApplied={() => { load(); }}
          />
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-10 text-center">
            <div className="text-emerald-300 text-lg">No drift detected in this category.</div>
            <div className="text-sm text-slate-400 mt-2">
              All parallel state machines are aligned for visible subscribers.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((item, idx) => {
              const links = buildNavLinks(item);
              return (
                <div
                  key={`${item.subject_id}-${item.category}-${idx}`}
                  data-testid={`drift-item-${item.category}`}
                  className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 flex items-start gap-4"
                >
                  <Badge className={`${SEVERITY_STYLE[item.severity] || SEVERITY_STYLE.low} border text-xs uppercase tracking-wider`}>
                    {item.severity}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-medium truncate">{item.name || item.subject_id}</span>
                      {item.email && <span className="text-xs text-slate-500 truncate">· {item.email}</span>}
                    </div>
                    <div className="text-sm text-slate-300 mb-2">{item.summary}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1 mb-3">
                      <span><Filter className="w-3 h-3 inline mr-1" />{CATEGORY_LABEL[item.category] || item.category}</span>
                      {Object.entries(item.details || {}).map(([k, v]) => (
                        <span key={k}>{k}: <code className="text-slate-300">{String(v)}</code></span>
                      ))}
                      {item.inquiry_id && <span>inquiry: <code className="text-slate-300">{item.inquiry_id}</code></span>}
                    </div>

                    {/* Read-only navigation links */}
                    {links.length > 0 && (
                      <div
                        className="flex flex-wrap gap-2 pt-2 border-t border-slate-800/60"
                        data-testid={`drift-nav-${item.category}`}
                      >
                        {links.map((lnk) => {
                          const Icon = lnk.icon;
                          return (
                            <Link
                              key={lnk.to + lnk.label}
                              to={lnk.to}
                              data-testid={lnk.testid}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                         border border-slate-700 bg-slate-900/60 text-xs text-slate-200
                                         hover:bg-slate-800 hover:border-slate-600 transition"
                            >
                              <Icon className="w-3.5 h-3.5" />
                              <span>{lnk.label}</span>
                              <ExternalLink className="w-3 h-3 opacity-60" />
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
